import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/styles';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg typography-btn-large transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-stroke-primary-subtle ring-offset-2 disabled:cursor-not-allowed disabled:pointer-events-none disabled:bg-overlay-primary disabled:text-tertiary [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-foreground dark:text-secondary-foreground text-primary-foreground hover:text-secondary-foreground active:text-secondary-foreground',
        secondary:
          'bg-background text-primary hover:bg-background/70 active:bg-background/70 hover:text-secondary active:text-secondary',
        'secondary-gray':
          'bg-background-secondary text-primary hover:bg-overlay-secondary active:bg-overlay-secondary hover:text-secondary active:text-secondary',
        tertiary:
          'bg-transparent text-primary hover:bg-overlay-secondary active:bg-overlay-secondary hover:text-secondary active:text-secondary',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        toggle:
          'bg-background typography-body text-primary border-2 border-background hover:border-stroke-primary-bold data-[focus=true]:border-stroke-primary-bold dark:hover:border-primary dark:data-[focus=true]:border-primary',
        flat: '',
      },
      size: {
        default: 'h-12 px-5 py-2',
        sm: 'h-10 rounded-md px-3 text-xs',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
